import { CLIENT_UPLOAD_LIMITS } from '@shared/client-uploads';
import type { ClientUploadPartTicket } from '@shared/client-uploads';
import MD5 from 'crypto-js/md5';
import WordArray from 'crypto-js/lib-typedarrays';
import Base64 from 'crypto-js/enc-base64';
import type { ClientUploadFile, ClientUploadSession } from '@shared/client-uploads';

export type UploadManifestFile = { id: string; fileName: string; sizeBytes: number; lastModified: number };
export type UploadDraft = {
  id: string;
  secret: string;
  files: UploadManifestFile[];
  message: string;
  submitted: boolean;
  /** Retained before dispatch so uncertain submit responses retry the same body. */
  submissionMessage?: string;
};
export type PublicUploadAuth = { token: string; getAccessToken: () => string; reauth: () => Promise<void> };

export class UploadRequestError extends Error {
  constructor(message: string, public status: number, public retryAfter: number = 0) { super(message); }
}

export function retryAfterMs(value: string | null): number {
  if (!value) return 0;
  const seconds = Number(value);
  return Math.max(0, Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now());
}

export function createPublicUploadApi(auth: PublicUploadAuth) {
  let renewal: Promise<void> | null = null;
  return async function request<T>(path: string, init: RequestInit = {}, secret?: string): Promise<T> {
    const attempt = () => {
      const headers = new Headers(init.headers);
      headers.set('X-Share-Access-Token', auth.getAccessToken());
      if (secret) headers.set('X-Upload-Secret', secret);
      return fetch(`/api/p/${encodeURIComponent(auth.token)}/client-uploads${path}`, {
        ...init, headers, credentials: 'include',
      });
    };
    let response = await attempt();
    if (response.status === 401) {
      if (!renewal) renewal = auth.reauth().finally(() => { renewal = null; });
      await renewal;
      if (init.signal?.aborted) throw new DOMException('Abgebrochen', 'AbortError');
      response = await attempt();
    }
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new UploadRequestError(body.error || `Anfrage fehlgeschlagen (${response.status}).`, response.status, retryAfterMs(response.headers.get('Retry-After')));
    return body as T;
  };
}
export type PublicUploadApi = ReturnType<typeof createPublicUploadApi>;
export function jsonBody(value: unknown): RequestInit {
  return { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(value) };
}
export function uploadSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = bytes > 0 ? Math.min(3, Math.floor(Math.log(bytes) / Math.log(1000))) : 0;
  return `${(bytes / 1000 ** i).toLocaleString('de-DE', { maximumFractionDigits: i ? 1 : 0 })} ${units[i]}`;
}
export function validateUploadFiles(files: readonly File[]): void {
  if (!files.length || files.length > CLIENT_UPLOAD_LIMITS.files) throw new Error(`Bitte 1 bis ${CLIENT_UPLOAD_LIMITS.files} Dateien auswählen.`);
  if (files.some(file => file.size < 1 || file.size > CLIENT_UPLOAD_LIMITS.fileBytes)) throw new Error('Jede Datei muss größer als 0 B und höchstens 200 GB sein.');
  if (files.reduce((sum, file) => sum + file.size, 0) > CLIENT_UPLOAD_LIMITS.sessionBytes) throw new Error('Pro Sendung sind höchstens 400 GB möglich.');
}
export function newUploadDraft(files: readonly File[]): UploadDraft {
  validateUploadFiles(files);
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return {
    id: crypto.randomUUID(), secret: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''),
    files: files.map(file => ({ id: crypto.randomUUID(), fileName: file.name, sizeBytes: file.size, lastModified: file.lastModified })),
    message: '', submitted: false,
  };
}
export function matchUploadFiles(manifest: readonly UploadManifestFile[], originals: readonly File[]): Map<string, File> {
  const remaining = [...originals];
  const matched = new Map<string, File>();
  for (const entry of manifest) {
    const index = remaining.findIndex(file => file.name === entry.fileName && file.size === entry.sizeBytes && file.lastModified === entry.lastModified);
    if (index !== -1) matched.set(entry.id, remaining.splice(index, 1)[0]);
  }
  return matched;
}
export function draftStorageKey(token: string): string { return `acolori:client-upload:v1:${token}`; }
export function loadUploadDraft(token: string): UploadDraft | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(draftStorageKey(token)) || 'null');
    if (!parsed || typeof parsed.id !== 'string' || !/^[a-f0-9]{64}$/.test(parsed.secret) || !Array.isArray(parsed.files)) return null;
    if (!parsed.files.length || parsed.files.length > CLIENT_UPLOAD_LIMITS.files || parsed.files.some((file: UploadManifestFile) =>
      typeof file.id !== 'string' || typeof file.fileName !== 'string' || !Number.isSafeInteger(file.sizeBytes) || file.sizeBytes < 1 ||
      file.sizeBytes > CLIENT_UPLOAD_LIMITS.fileBytes || !Number.isFinite(file.lastModified))) return null;
    return { ...parsed, message: typeof parsed.message === 'string' ? parsed.message : '', submitted: parsed.submitted === true };
  } catch { return null; }
}
export function persistUploadDraft(token: string, draft: UploadDraft | null): void {
  if (draft) localStorage.setItem(draftStorageKey(token), JSON.stringify(draft));
  else localStorage.removeItem(draftStorageKey(token));
}
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('Abgebrochen', 'AbortError')); return; }
    const abort = () => { clearTimeout(timer); reject(new DOMException('Abgebrochen', 'AbortError')); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, ms);
    signal.addEventListener('abort', abort, { once: true });
  });
}
export async function withUploadRetry<T>(action: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try { return await action(); }
    catch (error) {
      if (signal.aborted || (error as Error).name === 'AbortError') throw error;
      const retryable = error instanceof TypeError || (error instanceof UploadRequestError && [423, 429, 503].includes(error.status));
      if (!retryable || attempt >= 3) throw error;
      const delay = error instanceof UploadRequestError ? error.retryAfter : 0;
      // Honor long Retry-After values by pausing rather than retrying early.
      if (delay > 60_000) throw new Error('Server ausgelastet. Bitte später mit „Fortsetzen“ erneut versuchen.');
      await sleep(Math.max(delay, Math.min(8000, 1000 * 2 ** attempt)), signal);
    }
  }
  throw new Error('Upload fehlgeschlagen.');
}

export async function uploadPartHash(part: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await part.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
/** This function has deliberately no same-origin/proxy fallback. */
export async function sendDirectPart(ticket: ClientUploadPartTicket, part: Blob, signal: AbortSignal): Promise<string> {
  if (signal.aborted) throw new DOMException('Abgebrochen', 'AbortError');
  const url = new URL(ticket.url);
  if (url.protocol !== 'https:' || !url.hostname.endsWith('.r2.cloudflarestorage.com') || url.username || url.password) {
    throw new Error('Ungültiges Upload-Ziel.');
  }
  if (!Number.isFinite(Date.parse(ticket.expiresAt)) || Date.parse(ticket.expiresAt) <= Date.now() + 30_000) {
    throw new UploadRequestError('Upload-Link abgelaufen. Neuer Link wird angefordert.', 503);
  }
  let response: Response;
  const requestAbort = new AbortController();
  const abort = () => requestAbort.abort();
  signal.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 10 * 60_000);
  try {
    response = await fetch(ticket.url, {
      method: 'PUT', body: part, headers: ticket.headers, signal: requestAbort.signal,
      credentials: 'omit', mode: 'cors', redirect: 'error', referrerPolicy: 'no-referrer',
    });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new UploadRequestError('R2 nicht erreichbar: Netzwerk, CORS oder abgelaufener Upload-Link. Es erfolgt kein Upload über den PM-Server.', 503);
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
  if (!response.ok) {
    throw new UploadRequestError(`R2-Upload fehlgeschlagen (${response.status}).`, [403,408,429,500,502,503,504].includes(response.status) ? 503 : response.status,
      retryAfterMs(response.headers.get('Retry-After')));
  }
  const etag = response.headers.get('ETag');
  if (!etag) throw new Error('R2 bestätigt den Upload, aber ETag fehlt. Bitte CORS ExposeHeaders: ["ETag"] prüfen. Kein Proxy-Fallback.');
  return etag;
}
/** Two file workers, sequential 32 MiB parts within each file. No bytes are persisted. */
export async function transferUpload(
  api: PublicUploadApi, draft: UploadDraft, session: ClientUploadSession, originals: Map<string, File>,
  signal: AbortSignal, progress: (fileId: string, uploadedBytes: number) => void,
  refresh: () => Promise<void>, completed?: (file: ClientUploadFile) => void,
): Promise<void> {
  const queue = session.files.filter(file => ['initializing', 'uploading', 'completing'].includes(file.state));
  for (const entry of queue) {
    if (!originals.has(entry.id)) throw new Error(`Zum Fortsetzen bitte die Originaldatei „${entry.fileName}“ erneut auswählen (Name, Größe und Änderungsdatum müssen übereinstimmen).`);
  }
  let index = 0;
  const worker = async () => {
    while (index < queue.length && !signal.aborted) {
      const entry = queue[index++];
      const original = originals.get(entry.id)!;
      const total = Math.ceil(entry.sizeBytes / CLIENT_UPLOAD_LIMITS.partBytes);
      const parts = new Set(entry.completedParts || []);
      const hashes = entry.completedPartHashes || {};
      // Validate ALL previously accepted parts before sending any new bytes for this file.
      for (const n of parts) {
        if (signal.aborted) throw new DOMException('Abgebrochen', 'AbortError');
        const start = (n - 1) * CLIENT_UPLOAD_LIMITS.partBytes;
        if (!hashes[String(n)] || await uploadPartHash(original.slice(start, Math.min(start + CLIENT_UPLOAD_LIMITS.partBytes, entry.sizeBytes))) !== hashes[String(n)]) {
          throw new Error(`Falsche Originaldatei: „${entry.fileName}“ stimmt inhaltlich nicht mit dem begonnenen Upload überein. Bitte die ursprüngliche Datei auswählen.`);
        }
      }
      for (let n = 1; n <= total; n++) {
        if (signal.aborted) throw new DOMException('Abgebrochen', 'AbortError');
        if (parts.has(n)) continue;
        const start = (n - 1) * CLIENT_UPLOAD_LIMITS.partBytes;
        const part = original.slice(start, Math.min(start + CLIENT_UPLOAD_LIMITS.partBytes, entry.sizeBytes));
        const hash = await uploadPartHash(part);
        const md5 = MD5(WordArray.create(new Uint8Array(await part.arrayBuffer()) as any)).toString(Base64);
        const path = `/sessions/${draft.id}/files/${entry.id}/parts/${n}`;
        const etag = await withUploadRetry(async () => {
          const ticket = await api<ClientUploadPartTicket>(`${path}/sign`, {
            ...jsonBody({ sha256: hash, contentMd5: md5, sizeBytes: part.size }), signal,
          }, draft.secret);
          return sendDirectPart(ticket, part, signal);
        }, signal);
        // Retrying a metadata ACK must not rewrite an already accepted R2 part.
        await withUploadRetry(() => api(`${path}/ack`, {
          ...jsonBody({ etag, sha256: hash }), signal,
        }, draft.secret), signal);
        parts.add(n);
        const uploaded = [...parts].reduce((sum, part) => sum + Math.min(CLIENT_UPLOAD_LIMITS.partBytes, entry.sizeBytes - (part - 1) * CLIENT_UPLOAD_LIMITS.partBytes), 0);
        progress(entry.id, uploaded);
      }
      const result = await withUploadRetry(() => api<ClientUploadFile>(`/sessions/${draft.id}/files/${entry.id}/complete`, { method: 'POST', signal }, draft.secret), signal);
      completed?.(result);
    }
  };
  await Promise.all([worker(), worker()]);
  if (!signal.aborted) await refresh();
}